import React, { useMemo } from "react";
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Divider,
  Icon,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import CallOutlinedIcon from "@mui/icons-material/CallOutlined";
import ChatOutlinedIcon from "@mui/icons-material/ChatOutlined";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import HearingOutlinedIcon from "@mui/icons-material/HearingOutlined";
import { OverlayView } from "@react-google-maps/api";
import {
  setClickToVideoCall,
  setClickToCall,
  useMaterialUIController,
} from "context";
import { telemetry } from "globals/endpoints";
import AuthenticationService from "application/modules/authentication/AuthenticationService";
import { setAmbientMode } from "context";

const MarkerPopup = ({
  userPaths,
  selectedMarker,
  setSelectedMarker,
  clickToMessageMarker,
  dragCallTransfer,
  handleClearEmergency,
  handleLiveMonitoring,
  setOpenUserRoute,
  setNotify,
  onContactAction,
}) => {
  if (!selectedMarker) return null;
  const position = useMemo(
    () => ({
      lat: parseFloat(selectedMarker.geoX.trim()),
      lng: parseFloat(selectedMarker.geoY.trim()),
    }),
    [selectedMarker.geoX, selectedMarker.geoY]
  );

  const [, dispatch] = useMaterialUIController();

  const batteryPercent = Math.max(
    0,
    Math.min(100, Number(selectedMarker.batteryPercentage) || 0),
  );
  const batteryTone =
    batteryPercent > 60 ? "#16a34a" : batteryPercent > 25 ? "#d97706" : "#dc2626";
  const isEmergencyActive =
    selectedMarker.emergencyStatus === "ManFallDown" ||
    selectedMarker.emergencyStatus === "Emergency";

  const compactActionButtonSx = (
    hoverShadow,
    hoverBackground,
    baseColor,
    hoverScale = 1.06,
    disableHover = false
  ) => ({
    width: 22,
    height: 22,
    minWidth: 22,
    minHeight: 22,
    p: 0.2,
    color: baseColor,
    border: "1px solid rgba(15, 23, 42, 0.12)",
    backgroundColor: "rgba(255, 255, 255, 0.9)",
    transition: "all 0.18s ease-in-out",
    "& .MuiSvgIcon-root, & .MuiIcon-root": {
      fontSize: 13,
      lineHeight: 1,
      transition: "color 0.18s ease-in-out",
    },
  });

  const clickToMarkerAmbient = (number) => {
    const payload = {
      kind: "D",
      sender: AuthenticationService.getExtensionNumber(),
      target: number,
      content: `callType: Ambient; callState: RequestAmbient;`,
      messageType: "call",
    };
    telemetry.post("chat/message", payload);
    setAmbientMode(dispatch, true);
    setSelectedMarker(null);
    setNotify({
      isOpen: true,
      type: "info",
      pagename: "Map",
      status: `Please wait... Ambient mode request sent to ${number}`,
    });
  };

  return (
    <>
      <OverlayView
        position={position}
        mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
      >
        <Box
          sx={{
            background:
              "linear-gradient(165deg, rgba(255,255,255,0.97), rgba(240,246,255,0.96))",
            p: 0.8,
            borderRadius: 2,
            border: "1px solid rgba(37, 99, 235, 0.2)",
            boxShadow: "0 14px 34px rgba(15, 23, 42, 0.2)",
            width: 248,
            textAlign: "left",
            backdropFilter: "blur(6px)",
            "& .MuiDivider-root": {
              my: 0.5,
              borderColor: "rgba(148, 163, 184, 0.28)",
            },
            "& .MuiTypography-root": {
              lineHeight: 1.2,
            },
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography
              sx={{
                fontSize: "0.76rem",
                fontWeight: 800,
                pr: 0.4,
                color: "#0b1220",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {selectedMarker.name}
            </Typography>
            {!selectedMarker.hover && (
              <IconButton
                size="small"
                sx={{
                  width: 18,
                  height: 18,
                  p: 0,
                  border: "1px solid rgba(239, 68, 68, 0.2)",
                  backgroundColor: "rgba(255,255,255,0.9)",
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    transform: "scale(1.08)",
                    boxShadow: "0 0 10px rgba(255, 72, 0, 0.55)",
                    backgroundColor: "rgba(239, 68, 68, 0.8)",
                    borderColor: "transparent",
                  },
                  "& .MuiSvgIcon-root, & .MuiIcon-root": {
                    transition: "color 0.2s ease-in-out",
                  },
                  "&:hover .MuiSvgIcon-root, &:hover .MuiIcon-root": {
                    color: "#fff",
                  },
                }}
                onClick={() => setSelectedMarker(null)}
              >
                <CloseIcon sx={{ fontSize: 12 }} color="error" />
              </IconButton>
            )}
          </Box>

            <Box
              sx={{
                mt: 0.2,
                mb: 0.5,
                p: 0.6,
                borderRadius: 1.2,
                border: "1px solid rgba(148, 163, 184, 0.26)",
                background: "rgba(255,255,255,0.78)",
              }}
            >
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography sx={{ fontSize: "0.56rem", fontWeight: 700, color: "#334155" }}>
                  Battery
                </Typography>
                <Typography sx={{ fontSize: "0.58rem", fontWeight: 800, color: batteryTone }}>
                  {batteryPercent}%
                </Typography>
              </Box>
              <Box
                sx={{
                  mt: 0.45,
                  width: "100%",
                  height: 6,
                  borderRadius: 999,
                  background: "rgba(148, 163, 184, 0.24)",
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    width: `${batteryPercent}%`,
                    height: "100%",
                    borderRadius: 999,
                    background: `linear-gradient(90deg, ${batteryTone}, ${batteryTone}CC)`,
                  }}
                />
              </Box>
            </Box>

          <Typography
              sx={{ fontSize: "0.56rem", fontWeight: 700, color: "#334155" }}
          >
              Last Update
          </Typography>
          <Typography
              sx={{ fontSize: "0.56rem", fontWeight: 600, color: "#0f172a", mt: 0.15 }}
          >
            {new Date(selectedMarker.updatedOn * 1000).toLocaleString("en-IN", {
              dateStyle: "medium",
              timeStyle: "short",
            })}
          </Typography>
          <Divider />
          <Typography
            sx={{ fontSize: "0.56rem", fontWeight: 600, color: "#334155" }}
          >
            Lat: {position.lat.toFixed(6)}, Lng: {position.lng.toFixed(6)}
          </Typography>
          <Divider />
          <Typography
            sx={{
              fontSize: "0.56rem",
              fontWeight: 600,
              color: "#475569",
              display: "-webkit-box",
              overflow: "hidden",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
            }}
          >
            {selectedMarker.address || "Fetching location..."}
          </Typography>
          <Divider />
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{
              gap: 0.55,
              background: "rgba(255,255,255,0.84)",
              border: "1px solid rgba(148, 163, 184, 0.28)",
              borderRadius: 1.4,
              px: 0.35,
              py: 0.22,
            }}
          >
            {/* <Tooltip title="Start Live Monitor">
              <IconButton
                color="success"
                onClick={() => {
                  handleLiveMonitoring(selectedMarker);
                  setSelectedMarker(null);
                }}
                sx={{
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    transform: "scale(1.1)",
                    boxShadow: "0 0 10px rgba(40, 167, 69, 0.7)",
                    backgroundColor: "rgba(40, 167, 69, 0.7)",
                  },
                  "& .MuiSvgIcon-root, & .MuiIcon-root": {
                    transition: "color 0.2s ease-in-out",
                  },
                  "&:hover .MuiSvgIcon-root, &:hover .MuiIcon-root": {
                    color: "#fff",
                  },
                }}
              >
                <Icon>person_pin_circle</Icon>
              </IconButton>
            </Tooltip> */}

            <Tooltip title="Audio Call">
              <IconButton
                onClick={() => {
                  const contactPayload = {
                    ...selectedMarker,
                    offEXtn: selectedMarker.extensionNumber,
                    extension: selectedMarker.extensionNumber,
                  };

                  if (typeof onContactAction === "function") {
                    onContactAction("call", contactPayload);
                  } else {
                    setClickToCall(dispatch, selectedMarker.extensionNumber);
                  }
                  setSelectedMarker(null);
                }}
                sx={{
                  width: 28,
                  height: 28,
                  border: (theme) =>
                    `1px solid ${alpha(theme.palette.success.main, 0.38)}`,
                  backgroundColor: (theme) =>
                    alpha(theme.palette.success.main, 0.12),
                  transition: "all .18s ease",
                  "&:hover": {
                    transform: "translateY(-1px)",
                    boxShadow: "0 6px 14px rgba(22, 163, 74, 0.28)",
                  },
                }}
              >
                <CallOutlinedIcon fontSize="small" color="success" />
              </IconButton>
            </Tooltip>

            <Tooltip title="Video Call">
              <IconButton
                onClick={() => {
                  const contactPayload = {
                    ...selectedMarker,
                    offEXtn: selectedMarker.extensionNumber,
                    extension: selectedMarker.extensionNumber,
                  };

                  if (typeof onContactAction === "function") {
                    onContactAction("video-call", contactPayload);
                  } else {
                    setClickToVideoCall(dispatch, selectedMarker.extensionNumber);
                  }
                  setSelectedMarker(null);
                }}
                sx={{
                  width: 28,
                  height: 28,
                  border: (theme) =>
                    `1px solid ${alpha(theme.palette.info.main, 0.38)}`,
                  backgroundColor: (theme) =>
                    alpha(theme.palette.info.main, 0.12),
                  transition: "all .18s ease",
                  "&:hover": {
                    transform: "translateY(-1px)",
                    boxShadow: "0 6px 14px rgba(2, 132, 199, 0.28)",
                  },
                }}
              >
                <VideocamOutlinedIcon fontSize="small" color="info" />
              </IconButton>
            </Tooltip>

            <Tooltip title="Ambient Mode">
              <IconButton
                onClick={() => {
                  const contactPayload = {
                    ...selectedMarker,
                    offEXtn: selectedMarker.extensionNumber,
                    extension: selectedMarker.extensionNumber,
                  };

                  if (typeof onContactAction === "function") {
                    onContactAction("ambient-mode", contactPayload);
                    setSelectedMarker(null);
                  } else {
                    clickToMarkerAmbient(selectedMarker.extensionNumber);
                  }
                }}
                sx={{
                  width: 28,
                  height: 28,
                  border: (theme) =>
                    `1px solid ${alpha(theme.palette.error.main, 0.38)}`,
                  backgroundColor: (theme) =>
                    alpha(theme.palette.error.main, 0.12),
                  transition: "all .18s ease",
                  "&:hover": {
                    transform: "translateY(-1px)",
                    boxShadow: "0 6px 14px rgba(239, 68, 68, 0.24)",
                  },
                }}
              >
                <HearingOutlinedIcon fontSize="small" color="error" />
              </IconButton>
            </Tooltip>

            <Tooltip title="Message">
              <IconButton
                onClick={() => {
                  const contactPayload = {
                    ...selectedMarker,
                    offEXtn: selectedMarker.extensionNumber,
                    extension: selectedMarker.extensionNumber,
                  };

                  if (typeof onContactAction === "function") {
                    onContactAction("message", contactPayload);
                  } else {
                    clickToMessageMarker(selectedMarker.extensionNumber);
                  }
                  setSelectedMarker(null);
                }}
                sx={{
                  width: 28,
                  height: 28,
                  border: (theme) =>
                    `1px solid ${alpha(theme.palette.warning.main, 0.38)}`,
                  backgroundColor: (theme) =>
                    alpha(theme.palette.warning.main, 0.12),
                  transition: "all .18s ease",
                  "&:hover": {
                    transform: "translateY(-1px)",
                    boxShadow: "0 6px 14px rgba(217, 119, 6, 0.24)",
                  },
                }}
              >
                <ChatOutlinedIcon fontSize="small" color="warning" />
              </IconButton>
            </Tooltip>

            {userPaths.length === 0 && (
              <Tooltip title="Route History">
                <IconButton
                  onClick={() => setOpenUserRoute(true)}
                  sx={{
                    width: 28,
                    height: 28,
                    border: (theme) =>
                      `1px solid ${alpha(theme.palette.info.main, 0.38)}`,
                    backgroundColor: (theme) =>
                      alpha(theme.palette.info.main, 0.12),
                    transition: "all .18s ease",
                    "&:hover": {
                      transform: "translateY(-1px)",
                      boxShadow: "0 6px 14px rgba(2, 132, 199, 0.28)",
                    },
                  }}
                >
                  <Icon sx={{ fontSize: 16, color: "#0284c7" }}>route</Icon>
                </IconButton>
              </Tooltip>
            )}

            {isEmergencyActive && (
              <Tooltip
                title={
                  selectedMarker.emergencyStatus === "Emergency"
                    ? "Clear SOS"
                    : "Clear Man Fall Down"
                }
              >
                <IconButton
                  onClick={() => {
                    handleClearEmergency(
                      selectedMarker.extensionNumber,
                      selectedMarker.emergencyStatus,
                    );
                    setSelectedMarker(null);
                  }}
                  sx={{
                    width: 28,
                    height: 28,
                    border: (theme) =>
                      `1px solid ${alpha(
                        selectedMarker.emergencyStatus === "Emergency"
                          ? theme.palette.error.main
                          : theme.palette.warning.main,
                        0.38,
                      )}`,
                    backgroundColor: (theme) =>
                      alpha(
                        selectedMarker.emergencyStatus === "Emergency"
                          ? theme.palette.error.main
                          : theme.palette.warning.main,
                        0.12,
                      ),
                    transition: "all .18s ease",
                    "&:hover": {
                      transform: "translateY(-1px)",
                      boxShadow:
                        selectedMarker.emergencyStatus === "Emergency"
                          ? "0 6px 14px rgba(239, 68, 68, 0.28)"
                          : "0 6px 14px rgba(217, 119, 6, 0.28)",
                    },
                  }}
                >
                  <Icon
                    sx={{
                      fontSize: 16,
                      color:
                        selectedMarker.emergencyStatus === "Emergency"
                          ? "#dc2626"
                          : "#d97706",
                    }}
                  >
                    clear
                  </Icon>
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>
      </OverlayView>
    </>
  );
};

export default MarkerPopup;
